import type { FC } from 'react';
import { CheckCircle2, ChevronRight, MessageCircle } from 'lucide-react';
import { Button, Cell, List, Section } from '@/shared/ui/primitives';
import { openLink } from '@/shared/telegram';
import { useQuery } from '@tanstack/react-query';

import { Page } from '@/app/Page';
import {
  PAYMENT_PHASE_CTA,
  subscriptionQueryOptions,
  useSubscriptionPayment,
} from '@/features/subscription';
import { formatDateForDisplay, parseCalendarDateInput } from '@/shared/lib/calendarDate';
import { SUPPORT_LINK } from '@/shared/lib/toastEvents';
import { QueryErrorPlaceholder } from '@/shared/ui';
import { TEST_IDS } from '@/testIds';
import styles from './AccountPages.module.css';

export const ProfilePage: FC = () => {
  const {
    data: subscription,
    isPending: isSubscriptionPending,
    isError,
    error,
    refetch,
  } = useQuery(subscriptionQueryOptions.active());
  const { subscribe, phase, isPending, isAwaitingConfirmation, needsSupport } =
    useSubscriptionPayment();

  const handleSubscribe = () => {
    if (needsSupport) {
      openLink(SUPPORT_LINK);
      return;
    }
    void subscribe().catch(() => undefined);
  };

  const renderSubscription = () => {
    if (isSubscriptionPending) return <Cell>Loading subscription...</Cell>;

    if (subscription) {
      // The API uses its maximum date when subscription access has no expiry.
      const hasNoExpiry = parseCalendarDateInput(subscription.endsAt) === '9999-12-31';
      return (
        <Cell
          leading={<CheckCircle2 size={22} aria-hidden="true" />}
          subtitle={
            hasNoExpiry ? 'No expiry' : `Active until ${formatDateForDisplay(subscription.endsAt)}`
          }
        >
          {subscription.name}
        </Cell>
      );
    }

    return (
      <Cell subtitle="Subscribe to restore access to all features">No active subscription</Cell>
    );
  };

  return (
    <Page back={false}>
      <header className={styles.header}>
        <h1>Profile</h1>
      </header>
      <List>
        {isError ? (
          <QueryErrorPlaceholder
            error={error}
            onRetry={() => void refetch()}
            header="Could not load subscription"
          />
        ) : (
          <>
            <Section header="Subscription" data-testid={TEST_IDS.profileSubscriptionSection}>
              {renderSubscription()}
            </Section>

            {!isSubscriptionPending && !subscription && (
              <Button
                variant="primary"
                fullWidth
                size="lg"
                loading={isPending}
                disabled={isPending || isAwaitingConfirmation}
                onClick={handleSubscribe}
                data-testid={TEST_IDS.profileSubscribeButton}
              >
                {PAYMENT_PHASE_CTA[phase]}
              </Button>
            )}
          </>
        )}
        <Section header="Support">
          <Cell
            leading={<MessageCircle size={22} aria-hidden="true" />}
            trailing={<ChevronRight size={18} aria-hidden="true" />}
            onClick={() => openLink(SUPPORT_LINK)}
            data-testid={TEST_IDS.profileSupportLink}
          >
            Contact support
          </Cell>
        </Section>
      </List>
    </Page>
  );
};
